import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CustomSelectableDropdown from '@components/CustomSelectableDropdown';
import ModalContainer from '@components/modals/ModalContainer';
import { toastError } from '@components/Toast';

import { generatePomListParams } from '@features/filter/static/filterUtil';
import AddPomImage from '@features/pom/components/AddPomImage';
import PomDetailsBrands from '@features/pom/components/PomDetailsBrands';

import { addItemInGlobalFilterData, updateGlobalFilterData } from '@store/actions/FilterAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import {
	fetchBrandBasicList,
	fetchDepartments,
	fetchDependentFilterList,
	fetchEnumValues,
	fetchSubCategories
} from '@services/commonService';
import {
	fetchPomInfo,
	fetchPomSerialList,
	fetchSingleVisual,
	onAddPom,
	onUpdate
} from '@services/pomService';

import { ORGANIZTION_INFO } from '@utils/Util';

const AddPomInfo = ({ type, onClose, editId }) => {
	const [images, setImages] = useState([]);
	const [isUploadCLick, setIsUploadClick] = useState(null);
	const [formData, setFormData] = useState(null);
	const [dropDownData, setDropdownData] = useState(null);
	const [searchBrand, setSearchBrand] = useState('');
	const [allBrandList, setAllBrandList] = useState([]);
	const [isBrandOnly, setIsBrandOnly] = useState(false);
	const [selectedBrandList, setSelectedBrandList] = useState([
		{
			brandId: [{ value: ORGANIZTION_INFO?.id, label: ORGANIZTION_INFO?.name }],
			pomCode: '',
			pomName: '',
			howToMeasure: '',
			tolerance: null
		}
	]);
	const [errors, setErrors] = useState({});
	const [deletedVisualIds, setDeletedVisualIds] = useState([]);
	const dispatch = useDispatch();
	let filterfields = useSelector((store) => store.filterFieldStore);

	const formatData = (data) =>
		data.map((item) => ({
			value: item.id || item.constant,
			label: item.name || item.number,
			isDisabled: item?.isUsed || null
		}));

	const onFetchDropdownValue = async () => {
		dispatch(openLoader());
		let data = {};
		data.pomBodyPart = formatData(await fetchEnumValues('info.nitex.app.measurement.PomBodyPart'));
		data.pomTbo = formatData(await fetchEnumValues('info.nitex.app.measurement.PomParentCategory'));
		data.pomPosition = formatData(await fetchEnumValues('info.nitex.app.measurement.PomPosition'));
		data.pomSide = formatData(await fetchEnumValues('info.nitex.app.measurement.PomSide'));
		data.pomSize = formatData(await fetchEnumValues('info.nitex.app.measurement.PomSize'));
		data.market = [
			{ value: '*', label: 'All', isDisabled: null },
			...formatData(await fetchDepartments())
		];
		data.categories = [
			{ value: '*', label: 'All', isDisabled: null },
			...formatData(await fetchSubCategories())
		];
		data.seialList = formatData(await fetchPomSerialList());
		setDropdownData(data);
		dispatch(closeLoader());
	};

	const fetchBrandList = async () => {
		const allBrands = await fetchBrandBasicList(`?name=${searchBrand}`);
		setAllBrandList(allBrands?.map((item) => ({ label: item.name, value: item.id })));
	};

	const onFetchData = async (editId) => {
		const data = await fetchPomInfo(editId);
		setFormData({
			...formData,
			bodyPart: formatData([data?.bodyPart]),
			marketIdList: formatData(data?.marketList),
			comment: data?.comment,
			categoryIdList: formatData(data?.subCategoryResponseList),
			parentCategory: formatData([data?.parentCategory]),
			position: formatData([data?.position]),
			serialId: [
				{
					value: data?.serial,
					label: data?.serial
					// isDisabled: item?.isUsed || null
				}
			],
			size: formatData([data?.size]),
			side: formatData([data?.side]),
			allMarket: data?.allMarket,
			allCategory: data?.allCategory
		});
		setImages(data?.visualResponseList);
		setIsBrandOnly(data?.brandOnly);
		const nitexItems = [];
		const otherItems = [];
		data.brandDetailsResponseList.forEach((item) => {
			const newItem = {
				...item,
				brandId: [{ value: item?.brandResponse?.id, label: item?.brandResponse?.name }]
			};
			if (item?.brandResponse?.name === ORGANIZTION_INFO?.name) {
				nitexItems.push(newItem);
			} else {
				otherItems.push(newItem);
			}
		});
		setSelectedBrandList([...nitexItems, ...otherItems]);
		setSelectedBrandList([...nitexItems, ...otherItems]);
	};

	useEffect(() => {
		onFetchDropdownValue();
		if (editId) {
			onFetchData(editId);
		}
	}, []);

	useEffect(() => {
		fetchBrandList();
	}, [searchBrand]);

	const onRemoveSinglePom = (id) => {
		setImages(images?.filter((item) => item.id !== id));
		setDeletedVisualIds([...deletedVisualIds, id]);
	};

	const renderUploadedImage = () => {
		return images?.map((item, idx) => (
			<div className="pob-single-box text-center d-flex justify-content-center flex-column" key={idx}>
				<div className="img">
					<img className="main-img" src={item?.documentResponse?.docUrl} alt="product" />
					<div className="action-icon cursor-pointer d-flex">
						<span onClick={() => setIsUploadClick(item)}>
							<img
								src={`${process.env.PUBLIC_URL}/icons/edit-black-20.svg`}
								alt="svg"
								width="20px"
								height="20px"
							/>
						</span>
						<span onClick={() => onRemoveSinglePom(item.id)}>
							<img
								src={`${process.env.PUBLIC_URL}/icons/delete_gray.svg`}
								alt="svg"
								width="20px"
								height="20px"
							/>
						</span>
					</div>
				</div>
				<div className="pom-add-content">
					<p className="gray_dark_02 regular-14">{item?.name}</p>
					<p className="gray_dark_02 regular-14">{item?.subCategoryName}</p>
				</div>
			</div>
		));
	};

	const onCloseUpload = () => setIsUploadClick(null);
	const onFetchSingleVisual = async (id) => {
		try {
			dispatch(openLoader());
			let responseData = await fetchSingleVisual(id);
			let data = images?.filter((item) => item.id !== responseData.id);
			setImages([...data, responseData]);
			dispatch(closeLoader());
		} catch (error) {
			dispatch(closeLoader());
			toastError(error.message);
		}
	};

	const getSelectedIds = (list, keyName = 'value') => list?.map((item) => item[keyName]);

	const isValidForm = (body) => {
		let {
			bodyPart,
			parentCategory,
			position,
			serialId,
			size,
			side,
			marketIdList,
			categoryIdList,
			visualIdList,
			brandDetailsList
		} = body;
		let newErrors = {};
		if (!bodyPart) {
			newErrors = { ...newErrors, bodyPartError: 'Body part is required' };
		}
		if (!marketIdList) {
			newErrors = { ...newErrors, marketError: 'Market is required' };
		}
		if (!categoryIdList) {
			newErrors = { ...newErrors, categoryError: 'Category is required' };
		}
		if (!parentCategory) {
			newErrors = { ...newErrors, tboError: 'TBO is required' };
		}
		if (!position) {
			newErrors = { ...newErrors, positionError: 'Position part is required' };
		}
		if (!serialId) {
			newErrors = { ...newErrors, serialError: 'Serial is required' };
		}
		if (!size) {
			newErrors = { ...newErrors, sizeError: 'Size is required' };
		}
		if (!side) {
			newErrors = { ...newErrors, sideError: 'Side is required' };
		}
		if (visualIdList?.length === 0) {
			newErrors = { ...newErrors, visualError: 'Visual is required' };
		}

		brandDetailsList.forEach((item, index) => {
			if (!item.brandId || item.brandId.length === 0) {
				newErrors[`brandId_${index}`] = 'Brand is required';
			}
			if (!item.pomCode) {
				newErrors[`pomCode_${index}`] = 'POM Code is required';
			}
			if (!item.pomName) {
				newErrors[`pomName_${index}`] = 'POM Name is required';
			}
		});

		// if (!selectedCategories[0]?.value) {
		// 	newErrors = { ...newErrors, categoryIdError: 'Category is required' };
		// }

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const onActionSucces = (responseData) => {
		let queryParams = generatePomListParams(filterfields, 0);

		dispatch(fetchDependentFilterList({ type: 'POM_LIST' }, queryParams));
		if (type === 'ADD') {
			dispatch(addItemInGlobalFilterData(responseData));
		} else dispatch(updateGlobalFilterData(responseData));
	};

	const onSubmit = async () => {
		const { bodyPart, parentCategory, position, serialId, size, side, marketIdList, categoryIdList } =
			formData || {};

		const getBodyData = (array) =>
			Array.isArray(array) && array.length > 0 ? getSelectedIds(array)[0] : null;

		const getFilteredIds = (list, data) => {
			return getBodyData(list) === '*'
				? getSelectedIds(data?.filter((item) => item?.value !== '*'))
				: getSelectedIds(list);
		};

		const body = {
			...formData,
			bodyPart: getBodyData(bodyPart),
			parentCategory: getBodyData(parentCategory),
			position: getBodyData(position),
			serialId: getBodyData(serialId),
			size: getBodyData(size),
			side: getBodyData(side),
			marketIdList: getFilteredIds(marketIdList, dropDownData?.market),
			categoryIdList: getFilteredIds(categoryIdList, dropDownData?.categories),
			visualIdList: getSelectedIds(images, 'id'),
			brandDetailsList: selectedBrandList?.map((item) => ({
				...item,
				brandId: getSelectedIds(item?.brandId)[0]
			})),
			brandOnly: isBrandOnly,
			deletedVisualIdList: deletedVisualIds
		};

		try {
			dispatch(openLoader());
			const isValid = isValidForm(body);
			if (!isValid) {
				dispatch(closeLoader());
				return;
			}
			const responseData = type === 'ADD' ? await onAddPom(body) : await onUpdate(body, editId);
			onActionSucces(responseData);
			setDeletedVisualIds([]);

			dispatch(closeLoader());

			onClose();
		} catch {
			dispatch(closeLoader());
		}
	};

	return (
		<ModalContainer className="add-pom-modal">
			<div className="common-popup">
				<div className="common-popup-header d-flex justify-content-between">
					<div className="popup-tilte d-flex align-items-center">
						<h3 className="semibold-16 mb-0">{type === 'ADD' ? 'Add' : 'Edit'} POM</h3>
					</div>
					<div className="close-btn" onClick={onClose}>
						<img src={`${process.env.PUBLIC_URL}/icons/close.svg`} alt="close" />
					</div>
				</div>
				<div className="common-popup-body scroll-y-label">
					{/* add-img */}
					<div className="pom-add-box scroll-x-label">
						<div
							className="pob-single-box pob-add-single text-center d-flex justify-content-center flex-column cursor-pointer"
							onClick={() => setIsUploadClick('ADD')}
						>
							<div className="img">
								<img
									src={`${process.env.PUBLIC_URL}/icons/plus-icon-large.svg`}
									alt="svg"
									width="24px"
									height="24px"
								/>
							</div>
							<div className="pom-add-content">
								<p className="gray_dark_02 regular-14">
									Add <br /> POM Visual
								</p>
							</div>
						</div>
						{renderUploadedImage()}
						{errors?.visualError && (
							<p className="error  d-flex align-items-center justify-content-center">
								{errors.visualError}
							</p>
						)}
					</div>
					{/* form */}
					<div className="pom-form">
						<div className="row">
							<div className="col-md-4 pr-2">
								<div className="form-group no-shadow-react-select size36">
									<label htmlFor="collection">Body Part *</label>
									<CustomSelectableDropdown
										allOptions={dropDownData?.pomBodyPart}
										setSelectedOptions={(data) => {
											setFormData({ ...formData, bodyPart: data });
										}}
										selectedOptions={formData?.bodyPart}
										isMulti={false}
									/>
									{errors.bodyPartError && <p className="error">{errors.bodyPartError}</p>}
								</div>
							</div>
							<div className="col-md-4 pr-2">
								<div className="form-group no-shadow-react-select size36">
									<label htmlFor="collection">Market *</label>
									<CustomSelectableDropdown
										allOptions={dropDownData?.market?.map((item) => ({
											...item,
											isDisabled:
												formData?.marketIdList?.length > 0 &&
												formData?.marketIdList[0]?.value === '*' &&
												item?.value !== '*'
										}))}
										setSelectedOptions={(data) => {
											if (data.some((item) => item.value === '*')) {
												setFormData({
													...formData,
													marketIdList: [
														{ value: '*', label: 'All', isDisabled: false }
													],
													allMarket: true
												});
											} else {
												setFormData({
													...formData,
													marketIdList: data,
													allMarket: false
												});
											}
										}}
										selectedOptions={
											formData?.allMarket
												? [
														{
															value: '*',
															label: 'All',
															isDisabled: false
														}
													]
												: formData?.marketIdList
										}
									/>
									{errors.marketError && <p className="error">{errors.marketError}</p>}
								</div>
							</div>
							<div className="col-md-4 pr-2">
								<div className="form-group no-shadow-react-select size36">
									<label htmlFor="collection">Category *</label>
									<CustomSelectableDropdown
										allOptions={dropDownData?.categories?.map((item) => ({
											...item,
											isDisabled:
												formData?.categoryIdList?.length > 0 &&
												formData?.categoryIdList[0]?.value === '*' &&
												item?.value !== '*'
										}))}
										setSelectedOptions={(data) => {
											if (data?.some((item) => item?.value === '*')) {
												setFormData({
													...formData,
													categoryIdList: [
														{ value: '*', label: 'All', isDisabled: false }
													],
													allCategory: true
												});
											} else {
												setFormData({
													...formData,
													categoryIdList: data,
													allCategory: false
												});
											}
										}}
										selectedOptions={
											formData?.allCategory
												? [
														{
															value: '*',
															label: 'All',
															isDisabled: false
														}
													]
												: formData?.categoryIdList
										}
										isSelectAll={true}
									/>
									{errors.categoryError && <p className="error">{errors.categoryError}</p>}
								</div>
							</div>
							<div className="col-md-4 pr-2">
								<div className="form-group no-shadow-react-select size36">
									<label htmlFor="collection">TBO *</label>
									<CustomSelectableDropdown
										allOptions={dropDownData?.pomTbo}
										setSelectedOptions={(data) => {
											setFormData({ ...formData, parentCategory: data });
										}}
										selectedOptions={formData?.parentCategory}
										isMulti={false}
									/>
									{errors.tboError && <p className="error">{errors.tboError}</p>}
								</div>
							</div>
							<div className="col-md-4 pr-2">
								<div className="form-group no-shadow-react-select size36">
									<label htmlFor="collection">Position *</label>
									<CustomSelectableDropdown
										allOptions={dropDownData?.pomPosition}
										setSelectedOptions={(data) => {
											setFormData({ ...formData, position: data });
										}}
										selectedOptions={formData?.position}
										isMulti={false}
									/>
									{errors.positionError && <p className="error">{errors.positionError}</p>}
								</div>
							</div>
							<div className="col-md-4 pr-2">
								<div className="form-group no-shadow-react-select size36">
									<label htmlFor="collection">Serial *</label>
									<CustomSelectableDropdown
										allOptions={dropDownData?.seialList}
										setSelectedOptions={(data) => {
											setFormData({ ...formData, serialId: data });
										}}
										selectedOptions={formData?.serialId}
										isMulti={false}
									/>
									{errors.serialError && <p className="error">{errors.serialError}</p>}
								</div>
							</div>
							<div className="col-md-4 pr-2">
								<div className="form-group no-shadow-react-select size36">
									<label htmlFor="collection">POM Size *</label>
									<CustomSelectableDropdown
										allOptions={dropDownData?.pomSize}
										setSelectedOptions={(data) => {
											setFormData({ ...formData, size: data });
										}}
										selectedOptions={formData?.size}
										isMulti={false}
									/>
									{errors.sizeError && <p className="error">{errors.sizeError}</p>}
								</div>
							</div>
							<div className="col-md-4 pr-2">
								<div className="form-group no-shadow-react-select size36">
									<label htmlFor="collection">Side *</label>
									<CustomSelectableDropdown
										allOptions={dropDownData?.pomSide}
										setSelectedOptions={(data) => {
											setFormData({ ...formData, side: data });
										}}
										selectedOptions={formData?.side}
										isMulti={false}
									/>
									{errors.sideError && <p className="error">{errors.sideError}</p>}
								</div>
							</div>
							<div className="col-md-4 pr-2">
								<div className="form-group">
									<label htmlFor="collection">Comment</label>
									<input
										type="text"
										className="w-100"
										placeholder="Critical POM"
										value={formData?.comment}
										onChange={(e) => {
											setFormData({ ...formData, comment: e.target.value });
										}}
									/>
								</div>
							</div>
						</div>
					</div>
					{/* pom-details */}
					<PomDetailsBrands
						allBrandList={allBrandList}
						selectedBrandList={selectedBrandList}
						setSelectedBrandList={setSelectedBrandList}
						setSearchBrand={setSearchBrand}
						isBrandOnly={isBrandOnly}
						setIsBrandOnly={setIsBrandOnly}
						errors={errors}
					/>
				</div>
				<div className="modal-footer">
					<div className="footer-buttons text-right">
						<button className="button gray-outline size32 ml-2" onClick={onClose}>
							Cancel
						</button>
						<button className="button size32 ml-2" onClick={onSubmit}>
							Submit
						</button>
					</div>
				</div>
				{isUploadCLick && (
					<AddPomImage
						isUploadCLick={isUploadCLick}
						onCloseUpload={onCloseUpload}
						onddSucces={(id) => onFetchSingleVisual(id)}
					/>
				)}
			</div>
		</ModalContainer>
	);
};

export default AddPomInfo;
